import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useCreateCampaign } from "@/services/campaign.query";
import { useAgent } from "@/services/assistant.query";
import { AgentCombobox } from "@/components/Agents/AgentCombobox";
import { usePhoneNumbers } from "@/hooks/use-phone-numbers";
import { useToast } from "@/hooks/use-toast";
import { CalendarIcon, Upload, X, Map } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { analyzePrompt } from "@/lib/prompt-template";
import type { BusinessHoursConfig } from "@/lib/business-hours";
import BusinessHoursPicker from "@/components/BatchCall/BusinessHoursPicker";
import CountryCombobox from "@/components/BatchCall/CountryCombobox";
import {
  autoMapHeaders,
  buildContacts,
  campaignVariables,
  columnLabel,
  type CsvRow,
  fileProblem,
  formProblem,
  type HeaderMapping,
  nextMinute,
  parseCsv,
  PHONE_VARIABLE,
  type Problem,
  scheduledAt,
  toNdjson,
} from "@/components/BatchCall/campaign-file";

interface CreateCampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
}

/**
 * A campaign from a CSV: its settings, the CSV's columns mapped to the agent's prompt variables, and its contacts
 * cleaned in the browser (every number to +E.164) into an NDJSON file. The file is uploaded first, then the
 * campaign is created with it, and the worker imports the contacts in the background — the dialog closes as soon
 * as the campaign exists.
 */
export default function CreateCampaignDialog({ open, onOpenChange, workspaceId }: CreateCampaignDialogProps) {
  const [campaignName, setCampaignName] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [defaultCountry, setDefaultCountry] = useState<string | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
  const [mapping, setMapping] = useState<HeaderMapping>({});
  const [runType, setRunType] = useState<"immediate" | "scheduled">("immediate");
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined);
  const [scheduledTime, setScheduledTime] = useState(() => nextMinute(new Date()));
  const [maxRetries, setMaxRetries] = useState(0);
  const [retryIntervals, setRetryIntervals] = useState<number[]>([]);
  const [businessHours, setBusinessHours] = useState<BusinessHoursConfig | null>(null);
  const [isParsingCsv, setIsParsingCsv] = useState(false);
  /** The upload's percent while the contacts file is on its way; null otherwise. */
  const [uploaded, setUploaded] = useState<number | null>(null);

  // A closed dialog starts again from nothing.
  useEffect(() => {
    if (!open) {
      setCampaignName("");
      setPhoneNumberId("");
      setAgentId("");
      setDefaultCountry(null);
      setCsvFile(null);
      setCsvHeaders([]);
      setCsvRows([]);
      setMapping({});
      setRunType("immediate");
      setScheduledDate(undefined);
      setScheduledTime(nextMinute(new Date()));
      setMaxRetries(0);
      setRetryIntervals([]);
      setBusinessHours(null);
      setIsParsingCsv(false);
    }
  }, [open]);

  const createCampaign = useCreateCampaign();
  // The picker pages `{ id, name }` rows; the prompt below needs the whole config, so the selected agent is
  // fetched on its own.
  const { data: selectedAgent } = useAgent(agentId || undefined);
  const { data: phoneNumbers } = usePhoneNumbers(workspaceId);
  const { toast } = useToast();
  const refuse = (problem: Problem) => toast({ variant: "destructive", ...problem });

  const outboundNumbers = phoneNumbers?.filter((pn) => pn.is_active) ?? [];

  // When a phone number is picked and has an outbound agent, that agent is picked too. The column is an FK, so an
  // id on the row is an agent of this workspace; the picker resolves its name without having paged to it.
  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumberId(value);
    const attachedAgentId = outboundNumbers.find((pn) => pn.id === value)?.outbound_agent_id;
    if (attachedAgentId) setAgentId(attachedAgentId);
  };

  // Every {{variable}} of the agent's prompt, inside a {% if %} branch too, with the dial target first.
  const promptVariables = useMemo(
    () => campaignVariables(analyzePrompt(selectedAgent?.config?.systemPrompt ?? "").variables),
    [selectedAgent],
  );

  // A fresh mapping is guessed from the column names; one the customer has made is kept.
  useEffect(() => {
    if (agentId && csvHeaders.length > 0 && Object.keys(mapping).length === 0) {
      setMapping(autoMapHeaders(promptVariables, csvHeaders));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, promptVariables, csvHeaders]);

  const clearFile = () => {
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setMapping({});
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".csv")) {
      refuse({ title: "Invalid file type", description: "Please upload a CSV file." });
      return;
    }
    clearFile();
    setCsvFile(file);
    setIsParsingCsv(true);
    try {
      const { headers, rows } = parseCsv(await file.text());
      setCsvHeaders(headers);
      setCsvRows(rows);
      toast({
        title: "CSV file loaded",
        description: `Loaded ${rows.length} contact${rows.length === 1 ? "" : "s"} from CSV file.`,
      });
    } catch (error) {
      refuse({ title: "Failed to parse CSV", description: (error as Error).message });
      clearFile();
    } finally {
      setIsParsingCsv(false);
    }
  };

  const handleRetryCountChange = (count: number) => {
    setMaxRetries(count);
    // Each retry starts at 5 minutes after the attempt before it.
    setRetryIntervals(count >= 2 ? Array(count).fill(5) : []);
  };

  const handleIntervalChange = (index: number, value: number) => {
    const next = [...retryIntervals];
    next[index] = Math.max(1, value);
    setRetryIntervals(next);
  };

  const handleSubmit = async () => {
    const problem = formProblem({
      campaignName,
      phoneNumberId,
      agentId,
      headers: csvHeaders,
      rows: csvRows,
      mapping,
      maxRetries,
      retryIntervals,
      businessHours,
      runType,
      scheduledDate,
    });
    if (problem) return refuse(problem);

    const { contacts, skipped } = buildContacts(csvRows, csvHeaders, mapping, defaultCountry);
    if (contacts.length === 0) {
      return refuse({
        title: "No valid contacts",
        description:
          "No contacts with valid phone numbers found. Please ensure phone numbers are in a valid format (e.g., (818) 422-6629, 310-922-9784, or +18184226629).",
      });
    }
    const ndjson = toNdjson(contacts);
    const tooBig = fileProblem(contacts.length, new Blob([ndjson]).size);
    if (tooBig) return refuse(tooBig);
    if (skipped > 0) {
      toast({
        title: `${skipped} row${skipped === 1 ? "" : "s"} skipped`,
        description: `${skipped} row${skipped === 1 ? " was" : "s were"} skipped due to invalid or missing phone numbers. ${contacts.length} contact${contacts.length === 1 ? "" : "s"} will be imported.`,
      });
    }

    setUploaded(0);
    try {
      await createCampaign.mutateAsync({
        settings: {
          workspace_id: workspaceId,
          campaign_name: campaignName.trim(),
          agent_id: agentId,
          phone_number_id: phoneNumberId,
          max_retries: maxRetries,
          retry_intervals: retryIntervals,
          scheduled_at: runType === "scheduled" ? scheduledAt(scheduledDate!, scheduledTime) : null,
          business_hours: businessHours,
          default_country: defaultCountry,
        },
        file: { name: csvFile!.name, ndjson, contacts: contacts.length },
        onProgress: setUploaded,
      });
      toast({
        title: "Campaign created",
        description: `Importing ${contacts.length.toLocaleString("en-US")} contact${contacts.length === 1 ? "" : "s"}. You can leave this page.`,
      });
      onOpenChange(false);
    } catch (error) {
      refuse({ title: "Failed to create campaign", description: (error as Error).message });
    } finally {
      setUploaded(null);
    }
  };

  const uploading = uploaded !== null;

  return (
    // While the contacts file is on its way the dialog stays: closing it would leave the campaign half made.
    <Dialog open={open} onOpenChange={(next) => !uploading && onOpenChange(next)}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Batch Campaign</DialogTitle>
          <DialogDescription>Set up a new outbound call campaign with CSV contacts.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="campaign-name">Campaign Name *</Label>
            <Input
              id="campaign-name"
              placeholder="Spring Sales Campaign"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="phone-number">Outbound Phone Number *</Label>
            <Select value={phoneNumberId} onValueChange={handlePhoneNumberChange}>
              <SelectTrigger id="phone-number">
                <SelectValue placeholder="Select a phone number" />
              </SelectTrigger>
              <SelectContent>
                {outboundNumbers.length > 0 ? (
                  outboundNumbers.map((phone) => (
                    <SelectItem key={phone.id} value={phone.id}>
                      {phone.phone_number} {phone.nickname && `(${phone.nickname})`}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">No phone numbers available</div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Default Country (optional)</Label>
            <CountryCombobox value={defaultCountry} onChange={setDefaultCountry} />
            <p className="text-xs text-muted-foreground">
              Used for CSV numbers without a country code. Leave blank to keep current behavior.
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="agent">AI Agent *</Label>
            <AgentCombobox id="agent" workspaceId={workspaceId} value={agentId || null} onChange={(id) => setAgentId(id ?? "")} />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="csv-upload">Upload CSV File *</Label>
            {isParsingCsv ? (
              <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                <div className="flex flex-col items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
                  <p className="text-sm text-gray-500">Parsing CSV file...</p>
                </div>
              </div>
            ) : !csvFile ? (
              <div className="flex items-center justify-center w-full">
                <label
                  htmlFor="csv-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-2 text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">CSV files only</p>
                  </div>
                  <input id="csv-upload" type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
                </label>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{csvFile.name}</span>
                  <span className="text-xs text-gray-500">({csvRows.length} contacts)</span>
                </div>
                <Button type="button" variant="ghost" size="sm" aria-label="Remove file" onClick={clearFile} disabled={uploading}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* The prompt's variables, once an agent is picked: mapped, still needed, or optional. */}
          {agentId && (
            <div className="grid gap-2 p-3 border rounded-lg bg-blue-50/50">
              <div className="flex items-center gap-2">
                <Map className="w-4 h-4 text-blue-600" />
                <Label className="text-sm font-semibold">Dynamic Variables Found in Agent Prompt</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                The following dynamic variables were found in the selected agent's prompt. You'll need to map CSV columns to these variables below.
              </p>
              <div className="flex flex-wrap gap-2 mt-2">
                {promptVariables.map((varName) => {
                  const isRequired = varName === PHONE_VARIABLE;
                  const isMapped = mapping[varName] !== undefined;
                  return (
                    <div
                      key={varName}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        isMapped
                          ? "bg-green-100 text-green-800 border border-green-300"
                          : isRequired
                            ? "bg-red-100 text-red-800 border border-red-300"
                            : "bg-gray-100 text-gray-800 border border-gray-300"
                      }`}
                    >
                      {`{{${varName}}}`}
                      {isRequired && <span className="ml-1 text-red-600">*</span>}
                      {isMapped && <span className="ml-1 text-green-600">✓</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Which column feeds each variable. One column may feed several. */}
          {agentId && csvHeaders.length > 0 && (
            <div className="grid gap-3 p-4 border rounded-lg bg-blue-50">
              <div className="flex items-center gap-2">
                <Map className="w-4 h-4 text-blue-600" />
                <Label className="text-sm font-semibold">Map CSV Headers to Dynamic Variables</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Map your CSV column headers to the agent's dynamic variables. <span className="font-semibold text-red-600">Only phone_number is required.</span>
              </p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-xs font-semibold text-muted-foreground pb-2 border-b">
                  <div>Dynamic Variable (from Agent)</div>
                  <div>CSV Column Header</div>
                </div>
                {promptVariables.map((varName) => (
                  <div key={varName} className="grid grid-cols-2 gap-3 items-center">
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">{`{{${varName}}}`}</code>
                      <Label className="text-sm font-medium" htmlFor={`map-${varName}`}>
                        {varName}
                        {varName === PHONE_VARIABLE && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                    </div>
                    {/* "" is Radix's "nothing picked" (the placeholder shows): the select stays controlled once mapped. */}
                    <Select value={mapping[varName] ?? ""} onValueChange={(value) => setMapping({ ...mapping, [varName]: value })}>
                      <SelectTrigger id={`map-${varName}`} className="w-full">
                        <SelectValue placeholder="Select CSV column" />
                      </SelectTrigger>
                      <SelectContent>
                        {csvHeaders.map((header) => (
                          <SelectItem key={header} value={header}>
                            {columnLabel(header, mapping, varName)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-2">
            <Label>When to Run Campaign *</Label>
            <RadioGroup value={runType} onValueChange={(value: "immediate" | "scheduled") => setRunType(value)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="immediate" id="immediate" />
                <Label htmlFor="immediate" className="font-normal cursor-pointer">Run immediately</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="scheduled" id="scheduled" />
                <Label htmlFor="scheduled" className="font-normal cursor-pointer">Schedule for later</Label>
              </div>
            </RadioGroup>
          </div>

          {runType === "scheduled" && (
            <div className="grid gap-2">
              <Label>Schedule Date & Time *</Label>
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-full justify-start text-left font-normal", !scheduledDate && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {scheduledDate ? format(scheduledDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={scheduledDate}
                      onSelect={setScheduledDate}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <Input
                  type="time"
                  aria-label="Schedule time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="w-32"
                />
              </div>
            </div>
          )}

          <BusinessHoursPicker value={businessHours} onChange={setBusinessHours} />

          <div className="grid gap-3 p-4 border rounded-lg">
            <Label className="text-sm font-semibold">Retry Settings</Label>
            <p className="text-xs text-muted-foreground">
              Configure retry attempts for contacts who don't answer. <span className="font-semibold">This excludes the initial call.</span> Select how many times to retry (2-6 times).
            </p>
            <div className="grid gap-2">
              <Label htmlFor="max-retries">Maximum Retries (2-6, excluding initial call)</Label>
              <Select value={maxRetries.toString()} onValueChange={(value) => handleRetryCountChange(parseInt(value))}>
                <SelectTrigger id="max-retries">
                  <SelectValue placeholder="Select retry count" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No retries</SelectItem>
                  {[2, 3, 4, 5, 6].map((count) => (
                    <SelectItem key={count} value={count.toString()}>
                      {count} retries (excluding initial call)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {maxRetries >= 2 && (
              <div className="grid gap-2 mt-2">
                <Label className="text-sm font-medium">Retry Intervals (minutes between attempts)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Set the time interval between each retry attempt. The first interval is between the initial call and first retry.
                </p>
                {retryIntervals.map((interval, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Label className="text-sm w-40 flex-shrink-0" htmlFor={`retry-${index}`}>
                      {index === 0 ? "Initial → Retry 1:" : `Retry ${index} → Retry ${index + 1}:`}
                    </Label>
                    <Input
                      id={`retry-${index}`}
                      type="number"
                      min="1"
                      value={interval}
                      onChange={(e) => handleIntervalChange(index, parseInt(e.target.value) || 1)}
                      className="flex-1"
                      placeholder="Minutes"
                    />
                    <span className="text-xs text-muted-foreground">minutes</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {uploading && (
            <div className="grid gap-2" role="status">
              <p className="text-sm text-muted-foreground">Uploading contacts… {uploaded}%</p>
              <Progress value={uploaded} aria-label="Upload progress" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={uploading}>
            {uploading ? "Creating..." : "Create Campaign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
